// ============================================================================
// EPISODES CONTEXT - Manages episode data from the database
// ============================================================================

import { createContext, useContext, useCallback, ReactNode, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as episodeService from "@/services/episodeService";
import { useShows } from "@/contexts/ShowsContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import type { EpisodeCreationSource } from "@/components/episode/EpisodeSourceBadge";

// ============================================================================
// TYPES
// ============================================================================

/** UI-friendly episode status for display */
export type UIEpisodeStatus = "pending" | "awaiting_contract" | "pending_approval" | "approved" | "processing" | "delivered" | "failed" | "deleting";

/** UI-friendly episode representation with computed display fields */
export interface UIEpisode {
  id: string;
  title: string;
  description: string;
  showId: string;
  showTitle: string;
  status: UIEpisodeStatus;
  publishDate: string;
  duration: string;
  durationSeconds: number;
  platforms: string[];
  audioUrl: string | null;
  artworkUrl: string | null;
  createdAt: string;
  /** True when episode came from an RSS/platform import — exempt from plan episode count. */
  importedFromRss: boolean;
  /** How the episode was created (display-only chip). */
  creationSource: EpisodeCreationSource;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Format seconds to human-readable duration (e.g., "1h 12m" or "45m")
 */
export function formatDuration(seconds: number | null): string {
  if (!seconds) return "—";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/**
 * Map database status to UI display status
 * - draft → pending (not yet processing)
 * - published → delivered (successfully distributed)
 */
export function mapServiceStatusToUI(status: string): UIEpisodeStatus {
  const statusMap: Record<string, UIEpisodeStatus> = {
    draft: "pending",
    awaiting_contract: "awaiting_contract",
    pending_approval: "pending_approval",
    approved: "approved",
    processing: "processing",
    published: "delivered",
    failed: "failed",
    deleting: "deleting",
  };
  return statusMap[status] || "pending";
}

// ============================================================================
// CONTEXT
// ============================================================================

interface EpisodesContextType {
  episodes: UIEpisode[];
  isLoading: boolean;
  error: Error | null;
  getEpisode: (id: string) => UIEpisode | undefined;
  deleteEpisode: (id: string) => Promise<void>;
  deleteEpisodes: (ids: string[]) => Promise<void>;
  refetch: () => void;
}

const EpisodesContext = createContext<EpisodesContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function EpisodesProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { shows } = useShows(); // Still used for fallback show title lookup
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [user, setUser] = useState<{ id: string } | null>(null);

  // Check auth state
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (!session) {
        queryClient.removeQueries({ queryKey: ['episodes'] });
        queryClient.removeQueries({ queryKey: ['episode-platforms'] });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Create a map of show IDs to titles for quick lookup
  const showTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    shows.forEach((show) => map.set(show.id, show.title));
    return map;
  }, [shows]);

  // Fetch episodes from database
  const {
    data: dbEpisodes,
    isLoading: isDbLoading,
    error: dbError,
    refetch,
  } = useQuery({
    queryKey: ["episodes", user?.id, isAdmin ? "admin" : "user"],
    queryFn: () => episodeService.getAllUserEpisodes(isAdmin),
    enabled: !!user,
  });

  // Fetch platform statuses for all episodes in a single batched query.
  // Memoized + keyed on a stable string so the query doesn't re-run on every render.
  const episodeIds = useMemo(() => dbEpisodes?.map((ep) => ep.id) ?? [], [dbEpisodes]);
  const episodeIdsKey = useMemo(() => episodeIds.join(","), [episodeIds]);
  const { data: platformStatusMap } = useQuery({
    queryKey: ["episode-platforms", episodeIdsKey],
    queryFn: () => episodeService.getEpisodePlatformStatuses(episodeIds),
    enabled: episodeIds.length > 0,
  });

  // Transform database episodes to UI episodes
  const episodes: UIEpisode[] = useMemo(() => {
    if (!dbEpisodes) return [];

    return dbEpisodes.map((ep) => {
      let status = mapServiceStatusToUI(ep.status);
      const platformEntries = platformStatusMap?.get(ep.id) ?? [];

      // Safety override: match EpisodeDetail logic
      if (status === "delivered" && platformEntries.length > 0) {
        const allFailed = platformEntries.every((p) => p.status === "failed");
        const noneSucceeded = !platformEntries.some((p) => p.status === "success");
        if (allFailed) status = "failed";
        else if (noneSucceeded) status = "processing";
      }

      return {
        id: ep.id,
        title: ep.title,
        description: ep.description,
        showId: ep.showId,
        showTitle: (ep as any).showTitle || showTitleMap.get(ep.showId) || "Unknown Show",
        status,
        publishDate: ep.publishDate || ep.createdAt,
        duration: formatDuration(ep.durationSeconds),
        durationSeconds: ep.durationSeconds || 0,
        platforms: platformEntries.map((p) => p.platform),
        audioUrl: ep.audioUrl,
        artworkUrl: ep.artworkUrl,
        createdAt: ep.createdAt,
        importedFromRss: ep.importedFromRss === true,
        creationSource: ep.creationSource ?? "upload",
      };
    });
  }, [dbEpisodes, showTitleMap, platformStatusMap]);

  const isLoading = user ? isDbLoading : false;
  const error = user ? dbError : null;

  const getEpisode = useCallback(
    (id: string): UIEpisode | undefined => {
      return episodes.find((ep) => ep.id === id);
    },
    [episodes]
  );

  // Delete episode mutation
  const deleteMutation = useMutation({
    mutationFn: episodeService.deleteEpisode,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["episodes"] });
      queryClient.invalidateQueries({ queryKey: ["episode-platforms"] });
    },
  });

  const deleteEpisode = useCallback(
    async (id: string): Promise<void> => {
      if (!user) {
        throw new Error("Must be logged in to delete an episode");
      }
      await deleteMutation.mutateAsync(id);
    },
    [user, deleteMutation]
  );

  const deleteEpisodes = useCallback(
    async (ids: string[]): Promise<void> => {
      if (!user) {
        throw new Error("Must be logged in to delete episodes");
      }
      // Delete episodes sequentially to avoid overwhelming the API
      for (const id of ids) {
        await deleteMutation.mutateAsync(id);
      }
    },
    [user, deleteMutation]
  );

  return (
    <EpisodesContext.Provider
      value={{
        episodes,
        isLoading,
        error: error as Error | null,
        getEpisode,
        deleteEpisode,
        deleteEpisodes,
        refetch,
      }}
    >
      {children}
    </EpisodesContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useEpisodes() {
  const context = useContext(EpisodesContext);
  if (context === undefined) {
    throw new Error("useEpisodes must be used within an EpisodesProvider");
  }
  return context;
}
